import { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { appRoutes } from './appRoutes';
import { AppRouteConfig, RouteDefinition, RouteSection } from '@/types/routes';
import { Loading } from '@/components/ui/loading';

// Fallback loading component for nested routes
const RouteFallback = () => <Loading fullPage />;

/**
 * Main application routes component
 * Renders all application routes using a recursive approach based on appRoutes config
 */
export const AppRoutes = () => {
  /**
   * Recursively render routes from configuration
   */
  const renderRouteConfig = (config: AppRouteConfig[]): React.ReactNode[] => {
    return config.flatMap((routeOrSection, idx) => {
      // Handle route sections (groups of routes)
      if ('sectionTitle' in routeOrSection) {
        const section = routeOrSection as RouteSection;
        // Skip rendering section title, just render its routes
        return renderRouteConfig(section.routes);
      }
      // Handle individual routes
      else if ('element' in routeOrSection) {
        const route = routeOrSection as RouteDefinition;
        const { path, element, index: isIndex, children } = route;
        // Use a combination of path and index for a unique key
        const key = `${path ?? 'index'}-${idx}`;

        // Wrap element in Suspense for lazy-loaded components
        const wrappedElement = <Suspense fallback={<RouteFallback />}>{element}</Suspense>;

        // Index routes
        if (isIndex) {
          return <Route key={key} index element={wrappedElement} />;
        }
        // Regular path-based routes with optional nested children
        else if (path !== undefined) {
          return (
            <Route key={key} path={path} element={wrappedElement}>
              {children && renderRouteConfig(children)}
            </Route>
          );
        }
      }
      // Return null for invalid entries or section headers
      return null;
    });
  };

  // Render the Routes component using the generated configuration
  return <Routes>{renderRouteConfig(appRoutes)}</Routes>;
};

// Add a default export if App.tsx expects one
export default AppRoutes;
